import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, Globe, BarChart3, Target, Trash2, Mail, MessageSquare,
  ChevronRight, Loader2, Shield, ArrowUpRight, Clock, CheckCircle, XCircle
} from 'lucide-react';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (user?.role !== 'super_admin') { navigate('/dashboard'); return; }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [sRes, uRes, subRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/submissions')
      ]);
      setStats(sRes.data);
      setUsers(uRes.data);
      setSubmissions(subRes.data);
    } catch {} finally { setLoading(false); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data? This cannot be undone.')) return;
    setDeleting(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      loadData();
    } catch {} finally { setDeleting(null); }
  };

  if (loading) return <div className="min-h-screen bg-[#F7F9FC]"><Navbar /><div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 text-[#0268C4] animate-spin" /></div></div>;

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-16">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-6 h-6 text-[#0268C4]" />
          <h1 data-testid="admin-heading" className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F2951]">Admin Panel</h1>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-white border border-[#E2E8F0] rounded-xl p-1 h-auto">
            <TabsTrigger data-testid="admin-tab-overview" value="overview" className="rounded-lg data-[state=active]:bg-[#0268C4] data-[state=active]:text-white px-4 py-2 text-sm">Overview</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-lg data-[state=active]:bg-[#0268C4] data-[state=active]:text-white px-4 py-2 text-sm">Users ({users.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-submissions" value="submissions" className="rounded-lg data-[state=active]:bg-[#0268C4] data-[state=active]:text-white px-4 py-2 text-sm">Submissions ({submissions.length})</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-6">
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Total Users', value: stats.total_users, icon: Users, color: '#0268C4' },
                  { label: 'Guest Audits', value: stats.total_guest_audits, icon: Globe, color: '#22C55E' },
                  { label: 'New Users (7d)', value: stats.users_last_7d, icon: ArrowUpRight, color: '#DDA73D' },
                  { label: 'Conversion Rate', value: `${stats.conversion_rate}%`, icon: Target, color: '#64748B' }
                ].map((stat, i) => (
                  <div key={i} data-testid={`admin-stat-${i}`} className="bg-white rounded-2xl border border-[#E2E8F0] p-5 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
                    <stat.icon className="w-5 h-5 mb-2" style={{ color: stat.color }} strokeWidth={1.5} />
                    <div className="font-heading text-2xl font-semibold text-[#0F2951]">{stat.value}</div>
                    <div className="text-xs text-[#64748B] mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Recent submissions */}
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
              <h3 className="font-heading text-base font-medium text-[#0F2951] mb-4">Recent Guest Audits</h3>
              <div className="space-y-2">
                {submissions.slice(0, 10).map((sub, i) => (
                  <div key={sub.id || i} data-testid={`admin-recent-${i}`} className="flex items-center gap-3 p-3 bg-[#F7F9FC] rounded-xl">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: sub.overall_score > 80 ? '#22C55E' : sub.overall_score > 50 ? '#DDA73D' : '#EF4444' }}>
                      {sub.overall_score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0F2951] truncate">{sub.business_name || 'Unnamed'} {sub.city ? `- ${sub.city}` : ''}</p>
                      <p className="text-xs text-[#64748B] truncate">{sub.website_url}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-[#64748B]">{sub.email || 'No email'}</p>
                      <p className="text-[10px] text-[#64748B]">{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    {sub.claimed ? <CheckCircle className="w-4 h-4 text-[#22C55E] flex-shrink-0" /> : <XCircle className="w-4 h-4 text-[#E2E8F0] flex-shrink-0" />}
                  </div>
                ))}
                {submissions.length === 0 && <p className="text-sm text-[#64748B] text-center py-4">No submissions yet</p>}
              </div>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="space-y-3">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
              <h3 className="font-heading text-base font-medium text-[#0F2951] mb-4">All Users</h3>
              <div className="space-y-2">
                {users.map((u, i) => (
                  <div key={u.id} data-testid={`admin-user-${i}`} className="flex items-center gap-3 p-3 bg-[#F7F9FC] rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-[#EFF3F8] flex items-center justify-center text-[#64748B] text-sm font-medium">
                      {(u.name || u.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0F2951]">{u.name || 'Unnamed'}</p>
                      <p className="text-xs text-[#64748B]">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-[#64748B]">{u.project_count || 0} projects</p>
                        <p className="text-[10px] text-[#64748B]">Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {u.consent_email && <Mail className="w-3.5 h-3.5 text-[#22C55E]" title="Email consent" />}
                        {u.consent_sms && <MessageSquare className="w-3.5 h-3.5 text-[#22C55E]" title="SMS consent" />}
                      </div>
                      <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] text-[#64748B]">{u.role}</Badge>
                      {u.role !== 'super_admin' && (
                        <button
                          data-testid={`admin-delete-user-${i}`}
                          onClick={() => deleteUser(u.id)}
                          disabled={deleting === u.id}
                          className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#64748B] hover:text-[#EF4444] transition-colors"
                        >
                          {deleting === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* SUBMISSIONS (CRM) */}
          <TabsContent value="submissions" className="space-y-3">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
              <h3 className="font-heading text-base font-medium text-[#0F2951] mb-4">All Guest Audit Submissions (CRM)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-bold uppercase tracking-[0.15em] text-[#64748B] border-b border-[#E2E8F0]">
                      <th className="pb-3 pr-4">Business</th>
                      <th className="pb-3 pr-4">Email</th>
                      <th className="pb-3 pr-4">Location</th>
                      <th className="pb-3 pr-4">Score</th>
                      <th className="pb-3 pr-4">Issues</th>
                      <th className="pb-3 pr-4">Converted</th>
                      <th className="pb-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EFF3F8]">
                    {submissions.map((sub, i) => (
                      <tr key={sub.id || i} data-testid={`admin-sub-row-${i}`} className="hover:bg-[#F7F9FC]">
                        <td className="py-3 pr-4">
                          <p className="font-medium text-[#0F2951]">{sub.business_name || '—'}</p>
                          <p className="text-xs text-[#64748B] truncate max-w-[200px]">{sub.website_url}</p>
                        </td>
                        <td className="py-3 pr-4 text-[#64748B]">{sub.email || '—'}</td>
                        <td className="py-3 pr-4 text-[#64748B]">{sub.city}{sub.state ? `, ${sub.state}` : ''}</td>
                        <td className="py-3 pr-4">
                          <span className="font-heading font-semibold" style={{ color: sub.overall_score > 80 ? '#22C55E' : sub.overall_score > 50 ? '#DDA73D' : '#EF4444' }}>
                            {sub.overall_score}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-[#64748B]">{sub.issue_count || 0}</td>
                        <td className="py-3 pr-4">
                          {sub.claimed ? <CheckCircle className="w-4 h-4 text-[#22C55E]" /> : <XCircle className="w-4 h-4 text-[#E2E8F0]" />}
                        </td>
                        <td className="py-3 text-[#64748B] text-xs">{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {submissions.length === 0 && <p className="text-sm text-[#64748B] text-center py-8">No submissions yet. Share the public audit URL to start collecting leads.</p>}
              </div>
            </div>

            {/* Data retention notice */}
            <div className="p-4 bg-[#EFF3F8] rounded-xl border border-[#E2E8F0]">
              <p className="text-xs text-[#64748B]">
                <strong>Data Retention:</strong> Guest audit data is automatically deleted after 30 days (MongoDB TTL index). User data expires 30 days after their last login. All data handling complies with GDPR, CAN-SPAM, CCPA, and TCPA regulations.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
